import React, { useState } from 'react'

import { observer } from 'mobx-react-lite'
import Accordion from '@mui/material/Accordion'
import AccordionDetails from '@mui/material/AccordionDetails'
import AccordionSummary from '@mui/material/AccordionSummary'
import Paper from '@mui/material/Paper'
import Stack from '@mui/system/Stack'
import { useTheme } from '@mui/material/styles'
import { Icon } from '@mxenabled/mx-icons'
import { InstitutionLogo, P, Text } from '@mxenabled/mxui'

import { getHoldingGainLoss, getHoldingMarketValue } from '../../core/utils/InvestmentUtil'
import ConnectionsDrawer from '../../accounts/components/ConnectionsDrawer'
import {
  Loader,
  useAccountStore,
  useGlobalCopyStore,
  useGlobalUiStore,
  useHoldingStore,
  useScreenSize,
} from '../../common'
import { EmptyStateFooterComponent } from './EmptyStateFooterComponent'
import InvestmentDetailsDrawer from './HoldingDrawer'
import HoldingsTable from './HoldingTable'

interface HoldingDetail {
  id: string
  description: string
  qty: number
  holding: string
  costBasis: number
  rawCostBasis: number
  totalGainLoss: number
  gainLossAvailable: boolean
  gainLossBasis: number
  marketValue: number
}

const Overview = () => {
  const { isMobile } = useScreenSize()
  const [expandedStates, setExpandedStates] = useState<Record<string, boolean>>({})
  const [openDrawer, setOpenDrawer] = useState(false)
  const { selectedAccountGuids } = useGlobalUiStore()
  const { visibleHoldings: holdings, updateHoldings } = useHoldingStore()
  const [showConnectionsWidget, setShowConnectionsWidget] = React.useState(false)

  const [accountHoldingsData, setAccountHoldingsData] = useState<{
    accounts: any
    holdings: HoldingDetail[]
  }>({
    accounts: [],
    holdings: [],
  })
  const { accounts: accountsCopy, investments: copy } = useGlobalCopyStore()
  const { isCopyLoaded } = useGlobalUiStore()
  const [editingHoldingId, setEditingHoldingId] = useState<string | null>(null)
  const [currentEditedCostBasis, setCurrentEditedCostBasis] = useState<string>('')

  const theme = useTheme()

  const isDarkMode = theme.palette.mode === 'dark'

  const gainDisplayColor = isDarkMode ? '#0AC295' : '#09A57F'

  const lossDisplayColor = isDarkMode ? 'grey.300' : 'grey.700'

  const gainLossDisplayColor = (value: number, type: string = 'lesserThan') => {
    if (type === 'greaterThan') {
      return value >= 0 ? gainDisplayColor : lossDisplayColor
    }
    return value <= 0 ? lossDisplayColor : gainDisplayColor
  }

  const handleCostBasisClick = (holdingId: string, currentCostBasis: number) => {
    setEditingHoldingId(holdingId)
    setCurrentEditedCostBasis(currentCostBasis.toFixed(2))
  }

  const handleCostBasisChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    // Allow only numbers and a single decimal point
    const value = event.target.value
    if (/^\d*\.?\d*$/.test(value)) {
      setCurrentEditedCostBasis(value)
    }
  }

  const handleCostBasisKeyDown = (event: React.KeyboardEvent<HTMLInputElement>, id: string) => {
    if (event.key === 'Enter') {
      handleCostBasisBlur(id)
    } else if (event.key === 'Escape') {
      setEditingHoldingId(null)
    }
  }

  const handleCostBasisBlur = (id: string) => {
    const holding = holdings.find(({ guid }) => guid === id)
    if (!holding) return

    const updatedCostBasis = Number(currentEditedCostBasis)
    updateHoldings({ ...holding, cost_basis: updatedCostBasis })

    if (isMobile) {
      const updatedHoldings = accountHoldingsData.holdings.map((h) =>
        h.id === id
          ? {
              ...h,
              costBasis: updatedCostBasis,
              rawCostBasis: updatedCostBasis,
              // Recompute gain/loss against the new basis so the drawer's %/$
              // stay consistent with the edited Cost Basis instead of going stale.
              gainLossBasis: updatedCostBasis,
              totalGainLoss: h.marketValue - updatedCostBasis,
              gainLossAvailable: true,
            }
          : h,
      )

      setAccountHoldingsData((prev) => ({
        ...prev,
        holdings: updatedHoldings,
      }))
    }

    setEditingHoldingId(null)
  }

  const handleAccordionChange =
    (accountId: string) => (_event: React.SyntheticEvent, isExpanded: boolean) => {
      setExpandedStates((prevStates) => ({
        ...prevStates,
        [accountId]: isExpanded,
      }))
    }

  const { visibleHoldings: allHoldings } = useHoldingStore()
  const { visibleInvestmentAccounts } = useAccountStore()

  const accounts = visibleInvestmentAccounts.filter(({ guid }) =>
    selectedAccountGuids.includes(guid),
  )

  const totalPortfolioValue = accounts.reduce((sum, account) => sum + (account.balance || 0), 0)

  const accountGuids = new Set(accounts.map(({ guid }) => guid))

  const updatedHoldings = allHoldings.filter(({ account_guid }) => accountGuids.has(account_guid))

  // Only holdings with a known cost basis contribute to the aggregate; holdings
  // whose basis is unknown are excluded rather than counted as 100% gain.
  const gainLoss = updatedHoldings.reduce((sum, holding) => {
    const { available, gainLoss: hl } = getHoldingGainLoss(holding)
    return available ? sum + hl : sum
  }, 0)

  // When no holding in the portfolio has a known basis, render "—" instead of a
  // fabricated $0.00 gain.
  const gainLossAvailable = updatedHoldings.some((holding) => getHoldingGainLoss(holding).available)

  const closeDrawer = () => {
    setOpenDrawer(false)
  }

  if (!isCopyLoaded) {
    return <Loader />
  }

  return (
    <Stack sx={{ alignItems: 'center', gap: 48, pt: 48, width: '100%' }}>
      {/* header content */}
      {!!accounts.length && (
        <Stack sx={{ alignItems: 'center' }}>
          <Text sx={{ color: 'text.secondary' }}>{copy.overview.total_portfolio}</Text>
          <P variant="h1">
            {totalPortfolioValue.toLocaleString('en-US', {
              currency: 'USD',
              maximumFractionDigits: 2,
              minimumFractionDigits: 2,
              style: 'currency',
            })}
          </P>
          <Stack sx={{ alignItems: 'center', flexDirection: 'row', gap: 8 }}>
            {!gainLossAvailable ? (
              <Text sx={{ color: 'text.secondary', fontWeight: 700 }} variant="body2">
                —
              </Text>
            ) : (
              <>
                <Icon
                  name={gainLoss >= 0 ? 'trending_up' : 'trending_down'}
                  size={20}
                  sx={{ color: gainLossDisplayColor(gainLoss, 'greaterThan') }}
                />
                <Text
                  sx={{ color: gainLossDisplayColor(gainLoss, 'greaterThan'), fontWeight: 700 }}
                  variant="body2"
                >
                  {gainLoss.toLocaleString('en-US', {
                    currency: 'USD',
                    maximumFractionDigits: 2,
                    minimumFractionDigits: 2,
                    style: 'currency',
                  })}
                </Text>
              </>
            )}
          </Stack>
        </Stack>
      )}

      {accounts.map((account) => {
        const accountHoldings: HoldingDetail[] = allHoldings
          .filter((holding) => holding.account_guid === account.guid)
          .map((holding) => {
            // Option A: the effective basis (cost_basis, else purchase_price)
            // drives both the Cost Basis column and gain/loss, so the row
            // reconciles. `available` is false only when neither is known.
            const { available, basis, gainLoss } = getHoldingGainLoss(holding)
            return {
              id: holding.guid,
              qty: holding.shares || 0,
              holding: holding.symbol || holding.description || 'Unknown',
              costBasis: basis,
              // The true persisted cost_basis (0 when unset) seeds the editor, so
              // an unmodified edit never writes the purchase_price fallback back
              // to Abacus as a user-set cost_basis.
              rawCostBasis: holding.cost_basis || 0,
              totalGainLoss: gainLoss,
              gainLossAvailable: available,
              gainLossBasis: basis,
              marketValue: getHoldingMarketValue(holding),
              description: holding.description || '',
            }
          })

        const hasHoldingsData = accountHoldings.length > 0

        const accountTotalValue = account.balance
        const accountGainLoss = accountHoldings.reduce(
          (sum, holding) => holding.totalGainLoss + sum,
          0,
        )

        // Mirror the per-holding rule at the account level: if no holding in the
        // account has a known cost basis, show "—" rather than a fabricated
        // $0.00 / 0.00% rollup.
        const accountGainLossAvailable = accountHoldings.some((h) => h.gainLossAvailable)

        const accountGainLossPercentage = accountTotalValue
          ? (accountGainLoss / accountTotalValue) * 100
          : 0

        // Mobile View: Use a simplified presentation
        if (isMobile) {
          return (
            <Paper
              key={account.guid}
              onClick={
                accountHoldings.length
                  ? () => {
                      setOpenDrawer(true)
                      setAccountHoldingsData({
                        holdings: accountHoldings,
                        accounts: account,
                      })
                    }
                  : undefined
              }
              sx={{
                width: '100%',
                overflow: 'hidden',
              }}
            >
              <Stack
                sx={{
                  border: 1,
                  borderColor: 'divider',
                  borderRadius: '8px',
                  ml: 16,
                  mr: 16,
                  overflow: 'hidden',
                  pt: 14,
                }}
              >
                <Stack
                  sx={{
                    alignItems: 'center',
                    borderBottom: 1,
                    borderColor: 'divider',
                    flexDirection: 'row',
                    pb: 14,
                    pl: 14,
                    pr: 14,
                  }}
                >
                  <InstitutionLogo
                    alt={account.institutionName || ''}
                    institutionGuid={account.institution_guid || ''}
                    size={32}
                  />
                  <Stack sx={{ overflow: 'hidden', pl: 13, pr: 13 }}>
                    <Text sx={{ color: 'text.secondary' }} variant="caption">
                      {account.memberName}
                    </Text>
                    <Text sx={{ color: 'text.primary', fontWeight: 600 }} variant="body1">
                      {account.user_name || account.name}
                    </Text>
                  </Stack>
                </Stack>

                <Stack
                  sx={{
                    alignItems: 'center',
                    flexDirection: 'row',
                    justifyContent: 'space-between',
                    pb: 8,
                    pt: 8,
                  }}
                >
                  <Stack
                    sx={{
                      alignItems: 'center',
                      borderRight: 1,
                      borderColor: 'divider',
                      flex: 1,
                      justifyContent: 'center',
                      pb: 14,
                      pl: 7,
                      pr: 7,
                      pt: 14,
                    }}
                  >
                    <Text sx={{ color: 'text.secondary', fontSize: 11 }} variant="body2">
                      {copy.overview.total_gain_Loss}
                    </Text>
                    <Stack
                      sx={{
                        alignItems: 'center',
                        flexDirection: 'row',
                        gap: 2,
                        justifyContent: 'flex-end',
                      }}
                    >
                      {!accountGainLossAvailable ? (
                        <Text sx={{ color: 'text.secondary' }} variant="body1">
                          —
                        </Text>
                      ) : (
                        <>
                          {accountGainLoss !== 0 && (
                            <Icon
                              name={accountGainLoss > 0 ? 'trending_up' : 'trending_down'}
                              size={20}
                              sx={{ color: gainLossDisplayColor(accountGainLoss) }}
                            />
                          )}
                          <Text
                            sx={{
                              color: gainLossDisplayColor(accountGainLoss),
                              fontWeight: 600,
                            }}
                            variant="body1"
                          >
                            {accountGainLossPercentage.toFixed(2)}%
                          </Text>
                          <Text sx={{ color: 'text.secondary' }} variant="caption">
                            |
                          </Text>
                          <Text
                            sx={{
                              color: gainLossDisplayColor(accountGainLoss),
                              fontWeight: 600,
                            }}
                            variant="body1"
                          >
                            {Math.abs(accountGainLoss).toLocaleString('en-US', {
                              currency: 'USD',
                              maximumFractionDigits: 2,
                              minimumFractionDigits: 2,
                              style: 'currency',
                            })}
                          </Text>
                        </>
                      )}
                    </Stack>
                  </Stack>

                  <Stack
                    sx={{
                      alignItems: 'center',
                      flex: 1,
                      justifyContent: 'center',
                      pb: 14,
                      pl: 7,
                      pr: 7,
                      pt: 14,
                    }}
                  >
                    <Text sx={{ color: 'text.secondary' }} variant="caption">
                      {copy.overview.total_value}
                    </Text>
                    <Text sx={{ color: 'text.primary', fontWeight: 600 }} variant="body1">
                      {accountTotalValue?.toLocaleString('en-US', {
                        currency: 'USD',
                        maximumFractionDigits: 2,
                        minimumFractionDigits: 2,
                        style: 'currency',
                      })}
                    </Text>
                  </Stack>
                </Stack>
              </Stack>
            </Paper>
          )
        }

        const disabledColor = isDarkMode ? 'grey.700' : 'grey.400'
        const logoColor = accountHoldings.length ? 'text.primary' : disabledColor

        return (
          <Accordion
            expanded={expandedStates[account.guid] === true}
            key={account.guid}
            onChange={hasHoldingsData ? handleAccordionChange(account.guid) : () => {}}
            sx={{
              border: 1,
              borderColor: 'divider',
              borderRadius: '4px',
              width: '100%',
            }}
          >
            <AccordionSummary
              aria-controls="panel1a-content"
              id="panel1a-header"
              sx={[
                expandedStates[account.guid] && {
                  borderBottom: 1,
                  borderColor: 'divider',
                  maxHeight: 88,
                },
              ]}
            >
              <Stack
                sx={{
                  alignItems: 'center',
                  flexDirection: 'row',
                  gap: 13,
                  p: 14,
                  width: '100%',
                }}
              >
                <Icon name={'expand_more'} size={20} sx={{ color: logoColor }} />
                <InstitutionLogo
                  alt={account.institutionName || ''}
                  institutionGuid={account.institution_guid || ''}
                  size={32}
                />

                <Stack sx={{ alignItems: 'flex-start' }}>
                  <Text sx={{ color: 'text.secondary' }} variant="caption">
                    {account.memberName}
                  </Text>
                  <Text sx={{ color: 'text.primary', fontWeight: 600 }} variant="body1">
                    {account.user_name || account.name}
                  </Text>
                </Stack>
              </Stack>

              <Stack sx={{ flexDirection: 'row' }}>
                <Stack sx={{ alignItems: 'flex-end', pb: 14, pl: 24, pr: 24, pt: 14 }}>
                  <Text sx={{ color: 'text.secondary' }} variant="caption">
                    {copy.overview.total_gain_Loss}
                  </Text>

                  <Stack
                    sx={{
                      alignItems: 'center',
                      flexDirection: 'row',
                      gap: 2,
                      justifyContent: 'flex-end',
                    }}
                  >
                    {!accountGainLossAvailable ? (
                      <Text sx={{ color: 'text.secondary' }} variant="body1">
                        —
                      </Text>
                    ) : (
                      <>
                        {accountGainLoss !== 0 && (
                          <Icon
                            name={accountGainLoss >= 0 ? 'trending_up' : 'trending_down'}
                            size={20}
                            sx={{ color: gainLossDisplayColor(accountGainLoss) }}
                          />
                        )}
                        <Text
                          sx={{
                            color: gainLossDisplayColor(accountGainLoss),
                            fontWeight: 600,
                          }}
                          variant="body1"
                        >
                          {accountGainLossPercentage.toFixed(2)}%
                        </Text>
                        <Text sx={{ color: 'text.secondary' }} variant="caption">
                          |
                        </Text>
                        <Text
                          sx={{
                            color: gainLossDisplayColor(accountGainLoss),
                            fontWeight: 600,
                          }}
                          variant="body1"
                        >
                          {Math.abs(accountGainLoss).toLocaleString('en-US', {
                            currency: 'USD',
                            maximumFractionDigits: 2,
                            minimumFractionDigits: 2,
                            style: 'currency',
                          })}
                        </Text>
                      </>
                    )}
                  </Stack>
                </Stack>

                <Stack sx={{ alignItems: 'flex-end', gap: 12, pb: 14, pl: 16, pr: 16, pt: 14 }}>
                  <Stack sx={{ alignItems: 'flex-end' }}>
                    <Text sx={{ color: 'text.secondary' }} variant="caption">
                      {copy.overview.total_value}
                    </Text>
                    <Text sx={{ color: 'text.primary', fontWeight: 600 }} variant="body1">
                      {accountTotalValue?.toLocaleString('en-US', {
                        currency: 'USD',
                        maximumFractionDigits: 2,
                        minimumFractionDigits: 2,
                        style: 'currency',
                      })}
                    </Text>
                  </Stack>
                </Stack>
              </Stack>
            </AccordionSummary>
            <AccordionDetails sx={{ bgcolor: 'background.default', p: 12 }}>
              <Paper sx={{ border: 1, borderColor: 'neutral.light' }}>
                <HoldingsTable
                  currentEditedCostBasis={currentEditedCostBasis}
                  data={accountHoldings}
                  editingHoldingId={editingHoldingId}
                  handleCostBasisBlur={handleCostBasisBlur}
                  handleCostBasisChange={handleCostBasisChange}
                  handleCostBasisClick={handleCostBasisClick}
                  handleCostBasisKeyDown={handleCostBasisKeyDown}
                />
              </Paper>
            </AccordionDetails>
          </Accordion>
        )
      })}

      {/* footer content */}
      <EmptyStateFooterComponent />

      {/* Drawer Component  */}
      <InvestmentDetailsDrawer
        accountHoldingsData={accountHoldingsData}
        currentEditedCostBasis={currentEditedCostBasis}
        editingHoldingId={editingHoldingId}
        handleCostBasisBlur={handleCostBasisBlur}
        handleCostBasisChange={handleCostBasisChange}
        handleCostBasisClick={handleCostBasisClick}
        handleCostBasisKeyDown={handleCostBasisKeyDown}
        isOpen={openDrawer}
        onClose={closeDrawer}
      />

      <ConnectionsDrawer
        onClose={() => setShowConnectionsWidget(false)}
        showConnectionsWidget={showConnectionsWidget}
        title={accountsCopy.manage_connections}
      />
    </Stack>
  )
}

export default observer(Overview)
